import { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Calendar, Users, MapPin, Clock, Plane, Train, Bus, Filter, Volume2, VolumeX, Quote, Youtube, Instagram, Image as ImageIcon, Video, FileText, ArrowRight, Play, User } from 'lucide-react'
import { useTheme } from '../contexts/ThemeContext'
import { useLanguage } from '../contexts/LanguageContext'
import { useTours, type Tour, getLocalizedText, getLocalizedArray, getLocalizedNumber } from '../hooks/useTours'
import { calculateTourStatus } from '../utils/tourStatus'
import { useTestimonials } from '../hooks/useTestimonials'

type TourStatus = 'ongoing' | 'upcoming' | 'past'


export function Home() {
  const { t, i18n } = useTranslation()
  const { isRTL, language } = useLanguage()
  const [filter, setFilter] = useState<TourStatus | 'all'>('all')
  const [showFilters, setShowFilters] = useState(false)
  const [isMuted, setIsMuted] = useState(true)
  const [isPlayerReady, setIsPlayerReady] = useState(false)
  const videoRef = useRef<HTMLDivElement>(null)
  const playerRef = useRef<any>(null)
  const { theme } = useTheme()
  const { tours, loading, error } = useTours()
  const { testimonials: allTestimonials, loading: testimonialsLoading } = useTestimonials()
  const currentLanguage = i18n.language
  const [playingVideo, setPlayingVideo] = useState<string | null>(null)
  
  // Get first 3 testimonials for home page preview
  const featuredTestimonials = allTestimonials.slice(0, 3)

  const getYouTubeId = (url: string) => {
    // Handle YouTube Shorts URLs (youtube.com/shorts/VIDEO_ID)
    if (url.includes('/shorts/')) {
      const shortsMatch = url.match(/\/shorts\/([^#&?\/]*)/)
      if (shortsMatch && shortsMatch[1].length === 11) {
        return shortsMatch[1]
      }
    }
    // Handle regular YouTube URLs
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
    const match = url.match(regExp)
    return match && match[2].length === 11 ? match[2] : url
  }

  const getYouTubeThumbnail = (videoId: string) => {
    return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
  }

  const filteredTours = filter === 'all' 
    ? tours 
    : tours.filter(tour => calculateTourStatus(tour.departureDate, tour.returnDate) === filter)

  // Initialize YouTube player
  useEffect(() => {
    const videoId = theme === 'light' ? 'J62rY9x4xQ0' : 'OISE3nnfSO4'
    
    // Clean up previous player
    if (playerRef.current && playerRef.current.destroy) {
      playerRef.current.destroy()
      playerRef.current = null
    }
    setIsPlayerReady(false)
    
    // Wait for YouTube IFrame API to load
    const initPlayer = () => {
      if (window.YT && window.YT.Player && videoRef.current) {
        playerRef.current = new window.YT.Player(videoRef.current, {
          videoId: videoId,
          playerVars: {
            autoplay: 1,
            mute: 1,
            loop: 1,
            playlist: videoId,
            controls: 0,
            showinfo: 0,
            rel: 0,
            modestbranding: 1,
            iv_load_policy: 3,
            fs: 0,
            cc_load_policy: 0,
            playsinline: 1,
            disablekb: 1,
            enablejsapi: 1,
            origin: window.location.origin
          },
          events: {
            onReady: (event: any) => {
              setIsPlayerReady(true)
              event.target.playVideo()
              // Set initial mute state
              if (isMuted && typeof event.target.mute === 'function') {
                event.target.mute()
              }
            }
          }
        })
      } else {
        // Retry if API not loaded yet
        setTimeout(initPlayer, 100)
      }
    }

    // Check if API is already loaded
    if (window.YT && window.YT.Player) {
      initPlayer()
    } else {
      // Wait for API to load
      window.onYouTubeIframeAPIReady = initPlayer
      if (!window.YT) {
        // API not loaded, wait for it
        const checkAPI = setInterval(() => {
          if (window.YT && window.YT.Player) {
            clearInterval(checkAPI)
            initPlayer()
          }
        }, 100)
        return () => clearInterval(checkAPI)
      }
    }

    return () => {
      if (playerRef.current && playerRef.current.destroy) {
        playerRef.current.destroy()
        playerRef.current = null
      }
    }
  }, [theme])

  // Update video mute state using API
  useEffect(() => {
    if (isPlayerReady && playerRef.current) {
      if (isMuted) {
        try {
          if (typeof playerRef.current.mute === 'function') {
            playerRef.current.mute()
          }
        } catch (e) {
          console.log('Mute error:', e)
        }
      } else {
        // On mobile, unmuting requires user interaction
        // The button click is the user interaction, so this should work
        try {
          if (typeof playerRef.current.unMute === 'function') {
            playerRef.current.unMute()
          }
          if (typeof playerRef.current.playVideo === 'function') {
            playerRef.current.playVideo()
          }
        } catch (e) {
          console.log('Unmute may require user interaction on mobile')
        }
      }
    }
  }, [isMuted, isPlayerReady])


  const getStatusColor = (status: TourStatus) => {
    switch (status) {
      case 'ongoing':
        return 'bg-primary text-white'
      case 'upcoming':
        return 'bg-blue-500 text-white'
      case 'past':
        return 'bg-gray-500 text-white'
    }
  }

  const getVehicleIcon = (vehicle: string) => {
    switch (vehicle) {
      case 'airplane':
        return <Plane className="h-4 w-4" />
      case 'train':
        return <Train className="h-4 w-4" />
      case 'bus':
        return <Bus className="h-4 w-4" />
    }
  }

  return (
    <div className="pt-16">
      {/* Hero Section with Video Background */}
      <section className="relative py-20 overflow-hidden">
            {/* Video Background */}
            <div className="absolute inset-0 z-0 w-full h-full">
              <div
                ref={videoRef}
                id="youtube-player-home"
                className="absolute top-0 left-0 w-full h-full border-0 youtube-iframe"
                style={{
                  width: '100vw',
                  height: '56.25vw', // 16:9 aspect ratio
                  minHeight: '100vh',
                  minWidth: '177.77vh', // 16:9 aspect ratio
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  zIndex: -1
                }}
              />
              
              {/* Dark overlay */}
              <div className="absolute inset-0 bg-black/40 z-0" />
            </div>

        {/* Mute/Unmute Toggle */}
        <div className="absolute top-6 right-6 z-10">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsMuted(!isMuted)}
            className="p-3 bg-black/50 backdrop-blur-sm rounded-full text-white hover:bg-black/70 transition-colors"
          >
            {isMuted ? (
              <VolumeX className="h-6 w-6" />
            ) : (
              <Volume2 className="h-6 w-6" />
            )}
          </motion.button>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-6xl font-bold text-white mb-6 drop-shadow-lg"
          >
            {t('home.hero.title')}
            <span className="text-primary block">{t('home.hero.subtitle')}</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl text-white/90 max-w-3xl mx-auto drop-shadow-md"
          >
            {t('home.hero.description')}
          </motion.p>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 bg-background border-b border-border">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <h2 className="text-2xl font-bold text-foreground">{t('home.availableTours')}</h2>
            
            <div className="flex items-center gap-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 px-4 py-2 bg-secondary rounded-lg hover:bg-secondary/80 transition-colors"
              >
                <Filter className="h-4 w-4" />
                <span>{t('home.filter')}</span>
              </motion.button>
            </div>
          </div>

          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="mt-4 flex flex-wrap gap-2"
            >
              {[
                { key: 'all', label: t('home.tours.filter.all') },
                { key: 'ongoing', label: t('home.tours.filter.ongoing') },
                { key: 'upcoming', label: t('home.tours.filter.upcoming') },
                { key: 'past', label: t('home.tours.filter.past') }
              ].map(({ key, label }) => (
                <motion.button
                  key={key}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setFilter(key as TourStatus | 'all')}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    filter === key
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-secondary text-foreground hover:bg-secondary/80'
                  }`}
                >
                  {label}
                </motion.button>
              ))}
            </motion.div>
          )}
        </div>
      </section>

      {/* Tours Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4">
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="text-center">
                <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-muted-foreground">{t('home.tours.loading')}</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex justify-center items-center py-20">
              <div className="text-center">
                <p className="text-destructive mb-4">{t('home.tours.error')}: {error}</p>
                <button 
                  onClick={() => window.location.reload()} 
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
                >
                  {t('home.retry')}
                </button>
              </div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredTours.map((tour, index) => (
                <motion.div
                  key={tour.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-card border border-border rounded-xl overflow-hidden hover:shadow-lg transition-shadow"
              >
                <Link to={`/tour/${tour.id}`}>
                  {/* Tour Image */}
                  <div className="h-48 overflow-hidden">
                    <img
                      src={tour.image}
                      alt={tour.title ? getLocalizedText(tour.title, currentLanguage) : 'Tour Image'}
                      className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  
                  <div className="p-6">
                    {/* Header */}
                    <div className={`flex items-start justify-between mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <div>
                        <h3 className="text-xl font-bold text-card-foreground mb-2" dir={isRTL ? 'rtl' : 'ltr'}>
                          {tour.title ? getLocalizedText(tour.title, currentLanguage) : 'Loading...'}
                        </h3>
                        <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(calculateTourStatus(tour.departureDate, tour.returnDate))}`}>
                          {t(`home.tours.status.${calculateTourStatus(tour.departureDate, tour.returnDate)}`)}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-primary price">{getLocalizedNumber(tour.price, language)}</div>
                        <div className="text-sm text-muted-foreground duration">{tour.duration}</div>
                      </div>
                    </div>

                    {/* Route Preview */}
                    <div className="mb-4">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                        <MapPin className="h-4 w-4" />
                        <span>Route</span>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap" dir={isRTL ? 'rtl' : 'ltr'}>
                        {tour.route && tour.route.slice(0, 3).map((segment, idx) => (
                          <div key={idx} className={`flex items-center gap-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <span className="text-sm text-card-foreground">{getLocalizedText(segment.from, currentLanguage)}</span>
                            {getVehicleIcon(segment?.vehicle?.en)}
                            {idx < 2 && <span className="text-muted-foreground">{isRTL ? '←' : '→'}</span>}
                          </div>
                        ))}
                        {tour.route && tour.route.length > 3 && (
                          <span className="text-sm text-muted-foreground">+{tour.route.length - 3} more</span>
                        )}
                      </div>
                    </div>

                    {/* Details */}
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground">
                          <span className="date">{new Date(tour.departureDate).toLocaleDateString()}</span> - <span className="date">{new Date(tour.returnDate).toLocaleDateString()}</span>
                        </span>
                      </div>
                    </div>

                    {/* Highlights */}
                    <div className="mt-4">
                      <div className="text-sm text-muted-foreground mb-2">{t('home.highlights')}</div>
                      <div className="text-sm text-card-foreground line-clamp-2" dir={isRTL ? 'rtl' : 'ltr'}>
                        {getLocalizedArray(tour.highlights, currentLanguage).slice(0, 2).join(', ')}
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
              ))}

              {filteredTours.length === 0 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center py-12"
                >
                  <div className="text-muted-foreground text-lg">{t('home.tours.noTours')}</div>
                </motion.div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Testimonials Section */}
      {featuredTestimonials.length > 0 && (
        <section className="py-16 bg-gradient-to-br from-secondary/30 via-secondary/20 to-transparent">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-10 gap-4">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
                  {t('home.testimonials.title', 'What Our Pilgrims Say')}
                </h2>
                <p className="text-muted-foreground text-lg">
                  {t('home.testimonials.subtitle', 'Hear from those who have journeyed with us')}
                </p>
              </div>
              {allTestimonials.length > 3 && (
                <Link
                  to="/testimonials"
                  className="group flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-all hover:gap-3"
                >
                  <span className="font-medium">{t('home.testimonials.seeMore', 'See More')}</span>
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
              )}
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {featuredTestimonials.map((testimonial, index) => {
                const getTestimonialIcon = (type: string) => {
                  switch (type) {
                    case 'youtube':
                      return <Youtube className="h-4 w-4" />
                    case 'instagram':
                      return <Instagram className="h-4 w-4" />
                    case 'image':
                      return <ImageIcon className="h-4 w-4" />
                    case 'video':
                      return <Video className="h-4 w-4" />
                    default:
                      return <FileText className="h-4 w-4" />
                  }
                }

                const renderTestimonialPreview = (testimonial: any) => {
                  switch (testimonial.type) {
                    case 'youtube':
                      const videoId = getYouTubeId(testimonial.content)
                      const isPlaying = playingVideo === testimonial.id
                      
                      if (isPlaying) {
                        return (
                          <div className="w-full aspect-video rounded-t-2xl overflow-hidden">
                            <iframe
                              src={`https://www.youtube.com/embed/${videoId}?autoplay=1`}
                              title="YouTube video player"
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                              className="w-full h-full"
                            />
                          </div>
                        )
                      }
                      
                      return (
                        <div 
                          className="relative w-full aspect-video rounded-t-2xl overflow-hidden group cursor-pointer bg-gradient-to-br from-primary/20 to-primary/10"
                          onClick={() => setPlayingVideo(testimonial.id)}
                        >
                          <img
                            src={getYouTubeThumbnail(videoId)}
                            alt="YouTube video thumbnail"
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement
                              target.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
                            }}
                          />
                          <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                            <motion.div
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center shadow-2xl"
                            >
                              <Play className="h-8 w-8 text-white ml-1" fill="white" />
                            </motion.div>
                          </div>
                          <div className="absolute bottom-2 right-2 bg-black/70 text-white px-2 py-1 rounded text-xs font-medium flex items-center gap-1">
                            <Youtube className="h-3 w-3" />
                            YouTube
                          </div>
                        </div>
                      )
                    case 'image':
                      return (
                        <div className="relative w-full aspect-video rounded-t-2xl overflow-hidden group">
                          <img
                            src={testimonial.content}
                            alt={testimonial.author_name || 'Testimonial'}
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      )
                    case 'video':
                      const isVideoPlaying = playingVideo === testimonial.id
                      if (isVideoPlaying) {
                        return (
                          <div className="w-full aspect-video rounded-t-2xl overflow-hidden">
                            <video
                              src={testimonial.content}
                              controls
                              autoPlay
                              className="w-full h-full object-cover"
                            >
                              Your browser does not support the video tag.
                            </video>
                          </div>
                        )
                      }
                      return (
                        <div 
                          className="relative w-full aspect-video rounded-t-2xl overflow-hidden group cursor-pointer bg-gradient-to-br from-primary/20 to-primary/10"
                          onClick={() => setPlayingVideo(testimonial.id)}
                        >
                          <video
                            src={testimonial.content}
                            className="w-full h-full object-cover"
                            preload="metadata"
                          >
                            Your browser does not support the video tag.
                          </video>
                          <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                            <motion.div
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              className="w-16 h-16 bg-primary rounded-full flex items-center justify-center shadow-2xl"
                            >
                              <Play className="h-8 w-8 text-white ml-1" fill="white" />
                            </motion.div>
                          </div>
                          <div className="absolute bottom-2 right-2 bg-black/70 text-white px-2 py-1 rounded text-xs font-medium flex items-center gap-1">
                            <Video className="h-3 w-3" />
                            Video
                          </div>
                        </div>
                      )
                    default:
                      return (
                        <div className="relative w-full p-4">
                          <Quote className="absolute top-2 left-2 h-10 w-10 text-primary/20" />
                          <div className="text-foreground line-clamp-4 relative z-10 pt-6 text-sm leading-relaxed" dir={isRTL ? 'rtl' : 'ltr'}>
                            {testimonial.content}
                          </div>
                        </div>
                      )
                  }
                }

                return (
                  <motion.div
                    key={testimonial.id}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    whileHover={{ y: -5 }}
                    className="group bg-card border border-border/50 rounded-2xl overflow-hidden hover:border-primary/50 hover:shadow-2xl transition-all duration-300 flex flex-col h-full"
                  >
                    {/* Content */}
                    <div className="relative w-full flex-shrink-0 overflow-hidden">
                      {renderTestimonialPreview(testimonial)}
                      {/* Type Badge */}
                      {testimonial.type !== 'text' && (
                        <div className="absolute top-3 left-3 z-10">
                          <div className="flex items-center gap-1.5 bg-black/70 backdrop-blur-sm text-white px-2.5 py-1 rounded-full text-xs font-medium">
                            {getTestimonialIcon(testimonial.type)}
                            <span className="capitalize">{testimonial.type}</span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Card Body */}
                    <div className="p-5 flex-1 flex flex-col">
                      {/* Author Info */}
                      {(testimonial.author_name || testimonial.author_location) && (
                        <div className="space-y-1">
                          {testimonial.author_name && (
                            <div className="flex items-center gap-2">
                              <User className="h-3.5 w-3.5 text-primary" />
                              <p className="font-semibold text-sm text-foreground">{testimonial.author_name}</p>
                            </div>
                          )}
                          {testimonial.author_location && (
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <MapPin className="h-3 w-3" />
                              <span>{testimonial.author_location}</span>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Text content preview for non-media types */}
                      {testimonial.type === 'text' && (
                        <div className="mt-3 pt-3 border-t border-border/50">
                          <p className="text-xs text-muted-foreground line-clamp-3" dir={isRTL ? 'rtl' : 'ltr'}>
                            {testimonial.content}
                          </p>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </section>
      )}
    </div>
  )
}
